/* The report tiers in one place: menu copy, masthead title, accent, and the
   run-time budget the progress bar counts down. The menu's "~N min" label is
   derived from runSeconds, so the promise and the countdown cannot drift. */

export const REPORT_TIERS = [
  {
    value: "normal",
    name: "Editorial letter",
    title: "The Editorial Letter",
    desc: "The verdict, what's working, the revision plan.",
    accent: "var(--qk-pink-deep)",
    timeMultiplier: 1,
    tokenMultiplier: 1,
  },
  {
    value: "craft",
    name: "Craft & voice",
    title: "Craft & Voice",
    desc: "Point of view, prose voice, themes and motifs.",
    accent: "var(--qk-violet)",
    // Unmeasured tier: interpolated from its section count (3 extra
    // sections vs developmental's 5).
    timeMultiplier: 1.3,
    tokenMultiplier: 1.25,
  },
  {
    value: "developmental",
    name: "Developmental",
    title: "The Developmental Report",
    desc: "Structure, characters, pacing: the story's bones.",
    accent: "var(--qk-clay)",
    pro: true,
    timeMultiplier: 1.45,
    tokenMultiplier: 1.4,
  },
  {
    value: "detailed",
    name: "Full assessment",
    title: "The Full Assessment",
    // The map is NOT the differentiator — developmental already ships it.
    // Continuity is the only section unique to this tier.
    desc: "Everything above, plus the continuity check.",
    accent: "var(--qk-moss)",
    pro: true,
    timeMultiplier: 1.5,
    tokenMultiplier: 2.1,
  },
];

export const reportTier = (value) => REPORT_TIERS.find((t) => t.value === value);

/* Which tier first produces each section, mirroring TEMPLATES in
   wss-service/app/reports_agent/schema.py. "Everything above" is only
   checkable if the report can say where each tab came from. */
const SECTION_TIER = {
  verdict: "normal",
  synopsis: "normal",
  strengths: "normal",
  revision_plan: "normal",
  reader_experience: "normal",
  pov_and_voice: "craft",
  themes_and_motifs: "craft",
  opening_and_ending: "craft",
  structure_and_plot: "developmental",
  characters: "developmental",
  pacing: "developmental",
  threads: "developmental",
  book_map: "developmental",
  continuity: "detailed",
  metrics: "detailed",
};

const TIER_RANK = REPORT_TIERS.reduce((m, t, i) => ({ ...m, [t.value]: i }), {});

/* A tab's provenance is the furthest tier among the sections actually in it,
   so it reflects THIS report rather than a hardcoded guess: the story tab is
   the plain synopsis in an editorial letter and structure-and-plot in a
   developmental one. Returns null when nothing is recognised. */
export function tierOfSections(keys = []) {
  let best = null;
  for (const k of keys) {
    const tier = SECTION_TIER[k];
    if (!tier) continue;
    if (best === null || TIER_RANK[tier] > TIER_RANK[best]) best = tier;
  }
  return best;
}

/* Run-time fit from the same 400+ runs, anchored at p75 (not the median) so
   a healthy run beats the countdown instead of tripping "taking longer than
   expected" on schedule: ~170s + 5.4s per 1000 words for the normal tier,
   tier and author-notes multipliers on top. Worst cases still run 2-3x —
   the bar's overtime creep covers that tail. */
const REPORT_BASE_SECONDS = 170;
const REPORT_SECONDS_PER_WORD = 0.0054;
const NOTES_TIME_MULTIPLIER = 1.6;

export function estimateReportSeconds(tierValue, wordCount, hasNotes = false) {
  const mult = reportTier(tierValue)?.timeMultiplier ?? 1;
  return Math.round((REPORT_BASE_SECONDS + REPORT_SECONDS_PER_WORD * (wordCount || 0)) * mult * (hasNotes ? NOTES_TIME_MULTIPLIER : 1));
}

export const tierEtaLabel = (tierValue, wordCount, hasNotes = false) =>
  `~${Math.max(1, Math.round(estimateReportSeconds(tierValue, wordCount, hasNotes) / 60))} min`;

/* Measured fit across 439 eval rows / 55 boards: the manuscript is read once
   but re-billed on every assistant turn, so run tokens ≈ 56k + 23/word for
   the normal tier; other tiers scale by their turn count. Capped at 1M so
   even a basic allowance can afford one detailed report. Raw totals —
   provider prompt caching makes real cost lower. */
export const REPORT_TOKEN_CAP = 1_000_000;
const REPORT_BASE_TOKENS = 56_000;
const REPORT_TOKENS_PER_WORD = 23;

export function estimateReportTokens(tierValue, wordCount) {
  const mult = reportTier(tierValue)?.tokenMultiplier ?? 1;
  return Math.min(REPORT_TOKEN_CAP, Math.round((REPORT_BASE_TOKENS + REPORT_TOKENS_PER_WORD * (wordCount || 0)) * mult));
}

export function formatTokens(n) {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1).replace(/\.0$/, "")}M`;
  return `${Math.max(1, Math.round(n / 1000))}K`;
}
